import { Flex, Box, Text, Icon } from '@chakra-ui/react';
import { Fragment, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RADAR_LINES } from '../../data/scoreRadar';
import { SnowflakeLayout } from '../../assets/icons';

const scale = 100 / RADAR_LINES.length;
const angel = 360 / RADAR_LINES.length;

const getPercentCoordinate = (radius: number | string, itemIndex: number) => {
  const currentAngle = (itemIndex * angel * Math.PI) / 180;
  const xPercent = 50 + (Number(radius) / 2) * Math.sin(currentAngle);
  const yPercent = 50 - (Number(radius) / 2) * Math.cos(currentAngle);
  return { xPercent, yPercent };
};

export const RadarChart = ({ userResults, ...restProps }: { userResults: (number | string)[]; [key: string]: any }) => {
  const { t } = useTranslation();
  const [chart, setChart] = useState<any[]>([]);
  const [path, setPath] = useState<string>();
  const CoordinateEl: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const prepareChart = useCallback(() => {
    if (CoordinateEl?.current) {
      const width = CoordinateEl.current.clientWidth;
      const height = CoordinateEl.current.clientHeight;

      const coord = userResults.map((val, i) => {
        const { xPercent, yPercent } = getPercentCoordinate(val, i);
        const x = (xPercent * width) / 100;
        const y = (yPercent * height) / 100;
        return { x, y };
      });

      let path = '';
      coord.forEach(({ x, y }, i) => {
        if (i === 0) {
          path += `M ${x} ${y}`;
        } else {
          path += ` L ${x} ${y}`;
        }
      });

      setPath(path);
      setChart(coord);
    }
  }, [userResults]);

  useEffect(() => {
    let observer: any;
    if (CoordinateEl?.current) {
      observer = new ResizeObserver(() => prepareChart());
      observer.observe(CoordinateEl.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [prepareChart]);

  const isMobile = document.documentElement.clientWidth < 500;

  return (
    <Flex
      position='relative'
      w='100%'
      maxW='600px'
      alignItems='center'
      justifyContent='center'
      p={{ base: '20%', sm: '15%' }}
      {...restProps}
    >
      <Box w='100%' position='relative' ref={CoordinateEl} sx={{ aspectRatio: '1 / 1' }} transform='translateY(5%)'>
        {/* Layouts */}
        {RADAR_LINES.map((el, i) => {
          return (
            <Fragment key={`snow-flake-layout-${i}`}>
              <Box
                width={`${100 - i * scale}%`}
                position='absolute'
                top='50%'
                left='50%'
                transform='translate(-50%, -50%)'
                sx={{ aspectRatio: '1 / 1' }}
              >
                <Text
                  position='absolute'
                  left='50%'
                  transform='translate(-50%, -50%)'
                  fontSize='sm'
                  color='gray.500'
                  zIndex={1}
                >
                  {(100 - i * scale).toFixed(0)}
                </Text>
                <SnowflakeLayout width='100%' height='100%' style={{ transform: 'scale(1.27)' }} />
              </Box>
            </Fragment>
          );
        })}

        {/* Radar */}
        <svg height='100%' width='100%' style={{ position: 'absolute', overflow: 'visible' }}>
          <defs>
            <linearGradient id='userGradient' gradientTransform='rotate(90)'>
              <stop offset='0%' stopColor='#27B6E01A' />
              <stop offset='100%' stopColor='#27B6E09A' />
            </linearGradient>
          </defs>

          {path ? <path d={`${path} Z`} stroke='#00CCFF' fill='url(#userGradient)' strokeWidth='2' /> : null}
          {chart
            ? chart.map(({ x, y }, i) => (
                <circle cx={x} cy={y} r='4' stroke='#fff' strokeWidth='2' fill='#00CCFF' key={`radar-points-${i}`} />
              ))
            : null}
        </svg>

        {/* Card label */}
        {RADAR_LINES.map((el, i) => {
          const { xPercent, yPercent } = getPercentCoordinate(isMobile ? 135 : 120, i);
          return (
            <Box
              key={`label-${i}`}
              position='absolute'
              textAlign='center'
              transform='translate(-50%, -50%)'
              left={`${xPercent}%`}
              top={`${yPercent}%`}
            >
              <Icon as={el?.icon}></Icon>
              <Text fontWeight='bold' fontSize='sm'>
                {t(el?.labelKey)}
              </Text>
            </Box>
          );
        })}
      </Box>
    </Flex>
  );
};
